import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import NavbarV2 from '../components/navbar-v2';
import FooterV2 from '../components/footer-v2';
import html2canvas from 'html2canvas';
import HatimTakipLogo from '../assets/images/takvim-logo-HT.png';
import CihedLogo from '../assets/images/cihed-takvim-logo.png';
import { API_BASE_URL } from '../config';

interface WebCuz {
  _id: string;
  hatimId: string;
  hatimAdi: string;
  cuzNo: number;
  fullName: string;
  phone?: string;
  email?: string;
  status: 'pending' | 'completed';
  okumaTuru: string;
  source?: 'web' | 'mobile' | 'guest' | 'unknown';
  createdAt: string;
  completedAt?: string;
}

interface GroupStats {
  total: number;
  pending: number;
  completed: number;
  completionRate: number;
}

// Cüz sayfa aralıkları
const CUZ_SAYFA_ARALIKLARI: { [key: number]: { baslangic: number; bitis: number } } = {
  1: { baslangic: 1, bitis: 20 }, 2: { baslangic: 21, bitis: 40 }, 3: { baslangic: 41, bitis: 60 },
  4: { baslangic: 61, bitis: 80 }, 5: { baslangic: 81, bitis: 100 }, 6: { baslangic: 101, bitis: 120 },
  7: { baslangic: 121, bitis: 140 }, 8: { baslangic: 141, bitis: 160 }, 9: { baslangic: 161, bitis: 180 },
  10: { baslangic: 181, bitis: 200 }, 11: { baslangic: 201, bitis: 220 }, 12: { baslangic: 221, bitis: 240 },
  13: { baslangic: 241, bitis: 260 }, 14: { baslangic: 261, bitis: 280 }, 15: { baslangic: 281, bitis: 300 },
  16: { baslangic: 301, bitis: 320 }, 17: { baslangic: 321, bitis: 340 }, 18: { baslangic: 341, bitis: 360 },
  19: { baslangic: 361, bitis: 380 }, 20: { baslangic: 381, bitis: 400 }, 21: { baslangic: 401, bitis: 420 },
  22: { baslangic: 421, bitis: 440 }, 23: { baslangic: 441, bitis: 460 }, 24: { baslangic: 461, bitis: 480 },
  25: { baslangic: 481, bitis: 500 }, 26: { baslangic: 501, bitis: 520 }, 27: { baslangic: 521, bitis: 540 },
  28: { baslangic: 541, bitis: 560 }, 29: { baslangic: 561, bitis: 580 }, 30: { baslangic: 581, bitis: 604 }
};

interface ScheduleItem {
  gun: string;
  cuz: number;
  sayfalar: string;
  tarih: string;
}

interface TakvimModalData {
  cuzNo: number;
  fullName: string;
  hatimAdi: string;
  okumaTuru: string;
  createdAt: string;
  groupStartDate: string;
  groupEndDate: string;
}

// 🎨 Kart Ayarları Interface
interface CardSettings {
  image: string | null;
  badge: string | null;
  badgeColor: string;
  shortDescription: string | null;
  fullDescription: string | null;
  isFeatured: boolean;
  priority: number;
  backgroundColor: string | null;
  showMemberCount: boolean;
  showLastMember: boolean;
}

const DEFAULT_CARD_SETTINGS: CardSettings = {
  image: null,
  badge: null,
  badgeColor: '#10B981',
  shortDescription: null,
  fullDescription: null,
  isFeatured: false,
  priority: 0,
  backgroundColor: null,
  showMemberCount: true,
  showLastMember: true
};
const AdminGroupDetail: React.FC = () => {
  const navigate = useNavigate();
  const { groupId } = useParams<{ groupId: string }>();
  const [loading, setLoading] = useState(true);
  const [cuzler, setCuzler] = useState<WebCuz[]>([]);
  const [stats, setStats] = useState<GroupStats>({ total: 0, pending: 0, completed: 0, completionRate: 0 });
  const [groupName, setGroupName] = useState('');
  const [groupStartDate, setGroupStartDate] = useState<string>('');
  const [groupEndDate, setGroupEndDate] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed'>('all');
  const [updatingCuz, setUpdatingCuz] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Takvim modal state
  const [takvimModal, setTakvimModal] = useState<TakvimModalData | null>(null);
  const [takvimTuru, setTakvimTuru] = useState<'daily' | 'weekly'>('weekly');
  const [takvimMode, setTakvimMode] = useState<'sabit' | 'donerli'>('sabit');
  const scheduleRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  
  // 🎨 Kart Ayarları State'leri
  const [activeTab, setActiveTab] = useState<'cuzler' | 'kartAyarlari'>('cuzler');
  const [cardSettings, setCardSettings] = useState<CardSettings>(DEFAULT_CARD_SETTINGS);
  const [cardSettingsLoading, setCardSettingsLoading] = useState(false);
  const [cardSettingsSaving, setCardSettingsSaving] = useState(false);
  const [cardSettingsError, setCardSettingsError] = useState<string | null>(null);
  const [cardSettingsSuccess, setCardSettingsSuccess] = useState<string | null>(null);
  const [webdeYayinla, setWebdeYayinla] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const user = localStorage.getItem('adminUser');

    if (!token || !user) {
      navigate('/admin/login');
      return;
    }

    const userData = JSON.parse(user);
    if (userData.role !== 'admin' && userData.role !== 'editor') {
      navigate('/admin/login');
      return;
    }

    fetchGroupCuzler();
    fetchCardSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, groupId]);

  // 🎨 Kart Ayarlarını Getir
  const fetchCardSettings = async () => {
    setCardSettingsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/card-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCardSettings(data.data.cardSettings || DEFAULT_CARD_SETTINGS);
          setWebdeYayinla(data.data.webdeYayinla || false);
          if (data.data.groupName && !groupName) {
            setGroupName(data.data.groupName);
          }
        }
      }
    } catch (err) {
      console.error('Kart ayarları alınamadı:', err);
    } finally {
      setCardSettingsLoading(false);
    }
  };

  // 🎨 Kart Ayarlarını Kaydet
  const saveCardSettings = async () => {
    setCardSettingsSaving(true);
    setCardSettingsError(null);
    setCardSettingsSuccess(null);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/card-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cardSettings)
      });

      const data = await response.json();

      if (data.success) {
        setCardSettingsSuccess('Kart ayarları başarıyla kaydedildi!');
        setTimeout(() => setCardSettingsSuccess(null), 3000);
      } else {
        setCardSettingsError(data.message || 'Kaydetme başarısız');
      }
    } catch (err) {
      setCardSettingsError('Sunucu ile bağlantı kurulamadı');
    } finally {
      setCardSettingsSaving(false);
    }
  };

  // 🖼️ Görsel Yükle
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Boyut kontrolü (2MB)
    if (file.size > 2 * 1024 * 1024) {
      setCardSettingsError('Görsel boyutu 2MB\'dan küçük olmalıdır');
      return;
    }

    // Base64'e çevir
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result as string;
      
      setCardSettingsSaving(true);
      try {
        const token = localStorage.getItem('adminToken');
        const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/card-image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ image: base64 })
        });

        const data = await response.json();

        if (data.success) {
          setCardSettings(prev => ({ ...prev, image: base64 }));
          setCardSettingsSuccess('Görsel başarıyla yüklendi!');
          setTimeout(() => setCardSettingsSuccess(null), 3000);
        } else {
          setCardSettingsError(data.message || 'Görsel yüklenemedi');
        }
      } catch (err) {
        setCardSettingsError('Görsel yüklenirken hata oluştu');
      } finally {
        setCardSettingsSaving(false);
      }
    };
    reader.readAsDataURL(file);
  };

  // 🗑️ Görseli Sil
  const deleteCardImage = async () => {
    if (!window.confirm('Görseli silmek istediğinize emin misiniz?')) return;

    setCardSettingsSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/card-image`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setCardSettings(prev => ({ ...prev, image: null }));
        setCardSettingsSuccess('Görsel silindi');
        setTimeout(() => setCardSettingsSuccess(null), 3000);
      } else {
        setCardSettingsError(data.message || 'Görsel silinemedi');
      }
    } catch (err) {
      setCardSettingsError('Görsel silinirken hata oluştu');
    } finally {
      setCardSettingsSaving(false);
    }
  };

  // 🌐 Web Yayını Değiştir
  const toggleWebYayini = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/web-yayini`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ webdeYayinla: !webdeYayinla })
      });

      const data = await response.json();

      if (data.success) {
        setWebdeYayinla(!webdeYayinla);
        setCardSettingsSuccess(`Hatim web sitesinde ${!webdeYayinla ? 'yayına alındı' : 'yayından kaldırıldı'}`);
        setTimeout(() => setCardSettingsSuccess(null), 3000);
      } else {
        setCardSettingsError(data.message || 'İşlem başarısız');
      }
    } catch (err) {
      setCardSettingsError('Sunucu ile bağlantı kurulamadı');
    }
  };

  const fetchGroupCuzler = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/groups/admin/web-cuzler?groupId=${groupId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401 || response.status === 403) {
        navigate('/admin/login');
        return;
      }

      const data = await response.json();

      if (data.success) {
        setCuzler(data.data);
        
        // Grup adını ve tarihlerini al
        if (data.groups && data.groups.length > 0) {
          const grp = data.groups[0];
          try { setGroupName(decodeURIComponent(grp.name)); } catch { setGroupName(grp.name); }
          if (grp.startDate) setGroupStartDate(grp.startDate);
          if (grp.endDate) setGroupEndDate(grp.endDate);
        } else if (data.data.length > 0) {
          try {
            setGroupName(decodeURIComponent(data.data[0].hatimAdi));
          } catch {
            setGroupName(data.data[0].hatimAdi);
          }
        }

        // İstatistikleri hesapla
        const total = data.data.length;
        const completed = data.data.filter((c: WebCuz) => c.status === 'completed').length;
        const pending = total - completed;
        const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

        setStats({ total, pending, completed, completionRate });
      } else {
        setError(data.message || 'Veriler alınamadı');
      }
    } catch (err) {
      setError('Sunucu ile bağlantı kurulamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleTamamla = async (hatimId: string, cuzNo: number, cuzId: string) => {
    setUpdatingCuz(cuzId);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_BASE_URL}/api/groups/admin/web-cuz/${hatimId}/${cuzNo}/tamamla`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        setCuzler(prev => prev.map(c =>
          c._id === cuzId ? { ...c, status: 'completed', completedAt: new Date().toISOString() } : c
        ));
        setStats(prev => ({
          ...prev,
          pending: prev.pending - 1,
          completed: prev.completed + 1,
          completionRate: Math.round(((prev.completed + 1) / prev.total) * 100)
        }));
      } else {
        alert(data.message || 'Güncelleme başarısız');
      }
    } catch (err) {
      alert('Sunucu hatası');
    } finally {
      setUpdatingCuz(null);
    }
  };

  const handleIptal = async (hatimId: string, cuzNo: number, cuzId: string, fullName: string) => {
    const confirmed = window.confirm(
      `${cuzNo}. cüzü iptal etmek istediğinize emin misiniz?\n\nBu işlem "${fullName}" adlı kişinin cüz atamasını kaldıracak ve cüz tekrar müsait hale gelecektir.`
    );
    
    if (!confirmed) return;
    
    setUpdatingCuz(cuzId);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${API_BASE_URL}/api/groups/admin/web-cuz/${hatimId}/${cuzNo}/iptal`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        // Cüzü listeden kaldır (artık web'den alınmış değil)
        setCuzler(prev => prev.filter(c => c._id !== cuzId));
        setStats(prev => {
          const newTotal = prev.total - 1;
          const newPending = prev.pending - 1;
          return {
            ...prev,
            total: newTotal,
            pending: newPending,
            completionRate: newTotal > 0 ? Math.round((prev.completed / newTotal) * 100) : 0
          };
        });
        alert(`Cüz ${cuzNo} başarıyla iptal edildi ve tekrar müsait hale getirildi.`);
      } else {
        alert(data.message || 'İptal işlemi başarısız');
      }
    } catch (err) {
      alert('Sunucu hatası');
    } finally {
      setUpdatingCuz(null);
    }
  };

  // ===== TAKVİM FONKSİYONLARI =====
  const generateSchedule = (cuzNo: number, startDateStr: string, endDateStr: string, mode: 'sabit' | 'donerli', type: 'daily' | 'weekly'): ScheduleItem[] => {
    const schedule: ScheduleItem[] = [];
    const startDate = new Date(startDateStr);
    const isDaily = type === 'daily';
    
    // Hafta/gün sayısını başlangıç-bitiş tarihinden hesapla
    let count = 30; // fallback
    if (endDateStr) {
      const endDate = new Date(endDateStr);
      const diffMs = endDate.getTime() - startDate.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      // İlk gün/hafta dahil: floor + 1 (fencepost)
      count = isDaily ? (diffDays + 1) : (Math.floor(diffDays / 7) + 1);
      if (count < 1) count = 30;
    }

    for (let i = 0; i < count; i++) {
      const currentCuzNo = mode === 'donerli' ? ((cuzNo - 1 + i) % 30) + 1 : cuzNo;
      const cuzSayfa = CUZ_SAYFA_ARALIKLARI[currentCuzNo];
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + (isDaily ? i : i * 7));

      schedule.push({
        gun: isDaily ? `${i + 1}. Gün` : `${i + 1}. Hafta`,
        cuz: currentCuzNo,
        sayfalar: `${cuzSayfa.baslangic}-${cuzSayfa.bitis}`,
        tarih: currentDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })
      });
    }
    return schedule;
  };

  const handleOpenTakvim = (cuz: WebCuz) => {
    setTakvimMode(cuz.okumaTuru === 'donerli' ? 'donerli' : 'sabit');
    setTakvimModal({
      cuzNo: cuz.cuzNo,
      fullName: cuz.fullName,
      hatimAdi: cuz.hatimAdi,
      okumaTuru: cuz.okumaTuru,
      createdAt: cuz.createdAt,
      groupStartDate: groupStartDate,
      groupEndDate: groupEndDate
    });
  };

  const handleDownloadSchedule = async () => {
    if (!scheduleRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(scheduleRef.current, {
        backgroundColor: '#ffffff',
        scale: 3,
        logging: false,
        useCORS: true,
        allowTaint: true,
        width: scheduleRef.current.scrollWidth,
        height: scheduleRef.current.scrollHeight,
      });
      const link = document.createElement('a');
      const safeName = takvimModal?.fullName.replace(/\s+/g, '-') || 'takvim';
      link.download = `okuma-takvimi-${safeName}-cuz${takvimModal?.cuzNo}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Takvim indirilemedi:', error);
      alert('Takvim indirilemedi. Lütfen tekrar deneyin.');
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredCuzler = cuzler
    .filter(c => {
      if (filter === 'all') return true;
      return c.status === filter;
    })
    .filter(c => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        c.fullName.toLowerCase().includes(search) ||
        c.cuzNo.toString().includes(search) ||
        (c.phone && c.phone.includes(search))
      );
    })
    .sort((a, b) => a.cuzNo - b.cuzNo);

  return (
    <>
      <NavbarV2 />
      <div className="bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-100 min-h-screen pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Back Button & Header */}
          <div className="mb-6">
            <Link
              to="/admin"
              className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-4"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Gruplara Dön
            </Link>
            <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              {groupName || 'Grup Detayı'}
            </h1>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-gray-500 text-xs mb-1">Toplam Cüz</p>
              <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-gray-500 text-xs mb-1">Bekleyen</p>
              <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-gray-500 text-xs mb-1">Tamamlanan</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-gray-500 text-xs mb-1">Tamamlanma</p>
              <p className="text-2xl font-bold text-indigo-600">%{stats.completionRate}</p>
            </div>
          </div>

          {/* 🎨 Tab Butonları */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setActiveTab('cuzler')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                activeTab === 'cuzler'
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              📖 Cüz Yönetimi
            </button>
            <button
              onClick={() => setActiveTab('kartAyarlari')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                activeTab === 'kartAyarlari'
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              🎨 Kart Ayarları
            </button>
          </div>

          {/* 🎨 Kart Ayarları Sekmesi */}
          {activeTab === 'kartAyarlari' && (
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
              <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                🎨 Web Kartı Özelleştirme
              </h2>

              {/* Başarı / Hata Mesajları */}
              {cardSettingsSuccess && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                  {cardSettingsSuccess}
                </div>
              )}
              {cardSettingsError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                  {cardSettingsError}
                </div>
              )}

              {cardSettingsLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-500">Ayarlar yükleniyor...</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Web Yayını Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
                    <div>
                      <h3 className="font-semibold text-gray-800">🌐 Web'de Yayınla</h3>
                      <p className="text-sm text-gray-600">Bu hatimi hatimtakip.com.tr/cuzal sayfasında göster</p>
                    </div>
                    <button
                      onClick={toggleWebYayini}
                      className={`relative w-14 h-8 rounded-full transition-colors ${
                        webdeYayinla ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                        webdeYayinla ? 'translate-x-7' : 'translate-x-1'
                      }`}></span>
                    </button>
                  </div>

                  {/* Kart Görseli */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">🖼️ Kart Görseli</label>
                    <div className="flex items-start gap-4">
                      {cardSettings.image ? (
                        <div className="relative">
                          <img 
                            src={cardSettings.image} 
                            alt="Kart görseli" 
                            className="w-32 h-20 object-cover rounded-xl border-2 border-gray-200"
                          />
                          <button
                            onClick={deleteCardImage}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="w-32 h-20 bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      <div>
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        <button
                          onClick={() => imageInputRef.current?.click()}
                          disabled={cardSettingsSaving}
                          className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors font-medium text-sm"
                        >
                          {cardSettingsSaving ? 'Yükleniyor...' : 'Görsel Yükle'}
                        </button>
                        <p className="text-xs text-gray-500 mt-1">Maks. 2MB, JPG/PNG</p>
                      </div>
                    </div>
                  </div>

                  {/* Badge Ayarları */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">🏷️ Badge Metni</label>
                      <input
                        type="text"
                        value={cardSettings.badge || ''}
                        onChange={(e) => setCardSettings(prev => ({ ...prev, badge: e.target.value || null }))}
                        placeholder="Örn: POPÜLER, YENİ, HIZLI OKUMA"
                        maxLength={30}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">🎨 Badge Rengi</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={cardSettings.badgeColor}
                          onChange={(e) => setCardSettings(prev => ({ ...prev, badgeColor: e.target.value }))}
                          className="w-12 h-12 rounded-xl border border-gray-200 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={cardSettings.badgeColor}
                          onChange={(e) => setCardSettings(prev => ({ ...prev, badgeColor: e.target.value }))}
                          className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none font-mono text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Açıklamalar */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">📝 Kısa Açıklama (Kart üzerinde)</label>
                    <input
                      type="text"
                      value={cardSettings.shortDescription || ''}
                      onChange={(e) => setCardSettings(prev => ({ ...prev, shortDescription: e.target.value || null }))}
                      placeholder="Kısa tanıtım metni..."
                      maxLength={200}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                    />
                    <p className="text-xs text-gray-500 mt-1">{(cardSettings.shortDescription || '').length}/200 karakter</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">📄 Detaylı Açıklama</label>
                    <textarea
                      value={cardSettings.fullDescription || ''}
                      onChange={(e) => setCardSettings(prev => ({ ...prev, fullDescription: e.target.value || null }))}
                      placeholder="Detaylı açıklama metni..."
                      maxLength={1000}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">{(cardSettings.fullDescription || '').length}/1000 karakter</p>
                  </div>

                  {/* Öncelik ve Öne Çıkarma */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">⭐ Öncelik Sırası</label>
                      <input
                        type="number"
                        value={cardSettings.priority}
                        onChange={(e) => setCardSettings(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                        min={0}
                        max={100}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                      />
                      <p className="text-xs text-gray-500 mt-1">Yüksek değer = üstte görünür</p>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                      <input
                        type="checkbox"
                        id="isFeatured"
                        checked={cardSettings.isFeatured}
                        onChange={(e) => setCardSettings(prev => ({ ...prev, isFeatured: e.target.checked }))}
                        className="w-5 h-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                      />
                      <label htmlFor="isFeatured" className="flex-1">
                        <span className="font-semibold text-gray-800">⭐ Öne Çıkar</span>
                        <p className="text-sm text-gray-600">Bu hatimi listenin en üstünde göster</p>
                      </label>
                    </div>
                  </div>

                  {/* Görünürlük Ayarları */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                      <input
                        type="checkbox"
                        id="showMemberCount"
                        checked={cardSettings.showMemberCount}
                        onChange={(e) => setCardSettings(prev => ({ ...prev, showMemberCount: e.target.checked }))}
                        className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label htmlFor="showMemberCount" className="text-gray-700">
                        👥 Katılımcı sayısını göster
                      </label>
                    </div>
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                      <input
                        type="checkbox"
                        id="showLastMember"
                        checked={cardSettings.showLastMember}
                        onChange={(e) => setCardSettings(prev => ({ ...prev, showLastMember: e.target.checked }))}
                        className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label htmlFor="showLastMember" className="text-gray-700">
                        🕐 Son katılanı göster
                      </label>
                    </div>
                  </div>

                  {/* Kaydet Butonu */}
                  <div className="pt-6 border-t border-gray-200">
                    <button
                      onClick={saveCardSettings}
                      disabled={cardSettingsSaving}
                      className={`w-full py-4 rounded-xl font-bold text-white transition-all ${
                        cardSettingsSaving
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
                      }`}
                    >
                      {cardSettingsSaving ? 'Kaydediliyor...' : '💾 Ayarları Kaydet'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Cüzler Sekmesi */}
          {activeTab === 'cuzler' && (
            <>
              {/* Progress Bar */}
          <div className="bg-white rounded-xl p-4 mb-6 shadow-sm border border-gray-100">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">İlerleme</span>
              <span className="font-semibold text-indigo-600">{stats.completed}/{stats.total} cüz</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${stats.completionRate}%` }}
              ></div>
            </div>
          </div>

          {/* Search & Filter */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            {/* Search */}
            <div className="flex-1 relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="İsim, cüz no veya telefon ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
              />
            </div>
            {/* Filter */}
            <div className="flex gap-2">
              {(['all', 'pending', 'completed'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === f
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {f === 'all' ? 'Tümü' : f === 'pending' ? 'Bekleyen' : 'Tamamlanan'}
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-red-600">
              {error}
            </div>
          )}

          {/* Loading */}
          {loading ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-500">Yükleniyor...</p>
            </div>
          ) : filteredCuzler.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-500">
                {searchTerm ? 'Arama sonucu bulunamadı' : 'Bu grupta henüz cüz alınmamış'}
              </p>
            </div>
          ) : (
            /* Cüz Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCuzler.map((cuz) => (
                <div
                  key={cuz._id}
                  className={`bg-white rounded-xl p-4 shadow-sm border-2 transition-all hover:shadow-md ${
                    cuz.status === 'completed' ? 'border-emerald-200' : 'border-amber-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center justify-center w-12 h-12 rounded-xl font-bold text-lg ${
                        cuz.status === 'completed'
                          ? 'bg-emerald-100 text-emerald-600'
                          : 'bg-amber-100 text-amber-600'
                      }`}>
                        {cuz.cuzNo}
                      </span>
                      <div>
                        <p className="font-semibold text-gray-800">{cuz.fullName}</p>
                        {cuz.phone && (
                          <p className="text-sm text-gray-500">{cuz.phone}</p>
                        )}
                        {cuz.source && (
                          <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${
                            cuz.source === 'mobile' ? 'bg-blue-100 text-blue-700' :
                            cuz.source === 'web' ? 'bg-purple-100 text-purple-700' :
                            cuz.source === 'guest' ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {cuz.source === 'mobile' ? '📱 Mobil' :
                             cuz.source === 'web' ? '🌐 Web' :
                             cuz.source === 'guest' ? '👤 Misafir' : cuz.source}
                          </span>
                        )}
                      </div>
                    </div>
                    {cuz.status === 'completed' ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                        ✓ Tamam
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        ⏳ Bekliyor
                      </span>
                    )}
                  </div>

                  <div className="text-xs text-gray-500 mb-3">
                    <p>📅 {formatDate(cuz.createdAt)}</p>
                    {cuz.completedAt && (
                      <p className="text-emerald-600">✅ {formatDate(cuz.completedAt)}</p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {cuz.status === 'pending' && (
                      <button
                        onClick={() => handleTamamla(cuz.hatimId, cuz.cuzNo, cuz._id)}
                        disabled={updatingCuz === cuz._id}
                        className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                          updatingCuz === cuz._id
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                        }`}
                      >
                        {updatingCuz === cuz._id ? 'İşleniyor...' : '✓ Tamamla'}
                      </button>
                    )}
                    <button
                      onClick={() => handleOpenTakvim(cuz)}
                      className="py-2 px-3 text-sm font-medium rounded-lg transition-colors bg-blue-100 text-blue-600 hover:bg-blue-200 border border-blue-200"
                      title="Okuma takvimini görüntüle ve indir"
                    >
                      📅
                    </button>
                    <button
                      onClick={() => handleIptal(cuz.hatimId, cuz.cuzNo, cuz._id, cuz.fullName)}
                      disabled={updatingCuz === cuz._id}
                      className={`${cuz.status === 'pending' ? '' : 'flex-1'} py-2 px-3 text-sm font-medium rounded-lg transition-colors ${
                        updatingCuz === cuz._id
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-red-100 text-red-600 hover:bg-red-200 border border-red-200'
                      }`}
                    >
                      {updatingCuz === cuz._id ? '...' : '✕ İptal Et'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
            </>
          )}
        </div>
      </div>

      {/* ===== TAKVİM MODAL ===== */}
      {takvimModal && (() => {
        const schedule = generateSchedule(takvimModal.cuzNo, takvimModal.groupStartDate || takvimModal.createdAt, takvimModal.groupEndDate, takvimMode, takvimTuru);
        const firstHalf = schedule.slice(0, Math.ceil(schedule.length / 2));
        const secondHalf = schedule.slice(Math.ceil(schedule.length / 2));
        const nameParts = takvimModal.fullName.split(' ');
        const ad = nameParts[0] || '';
        const soyad = nameParts.slice(1).join(' ') || '';

        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between rounded-t-2xl z-10">
                <h3 className="text-lg font-bold text-gray-800">
                  📅 Okuma Takvimi — {takvimModal.fullName} ({takvimModal.cuzNo}. Cüz)
                </h3>
                <button
                  onClick={() => setTakvimModal(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500"
                >
                  ✕
                </button>
              </div>

              {/* Seçenekler */}
              <div className="p-4 border-b border-gray-100 flex flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 font-medium">Tür:</span>
                  <button
                    onClick={() => setTakvimMode('sabit')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      takvimMode === 'sabit' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    📌 Sabit
                  </button>
                  <button
                    onClick={() => setTakvimMode('donerli')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      takvimMode === 'donerli' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    🔄 Dönerli
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 font-medium">Periyot:</span>
                  <button
                    onClick={() => setTakvimTuru('daily')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      takvimTuru === 'daily' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    📅 Günlük
                  </button>
                  <button
                    onClick={() => setTakvimTuru('weekly')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      takvimTuru === 'weekly' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    📆 Haftalık
                  </button>
                </div>
              </div>

              {/* Takvim İçeriği (indirilebilir alan) */}
              <div className="p-4 overflow-x-auto">
                <div ref={scheduleRef} style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '24px', border: '1px solid #bbf7d0', minWidth: '600px' }}>
                  {/* Header with Logos */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #bbf7d0' }}>
                    <img src={HatimTakipLogo} alt="HatimTakip" style={{ height: '48px', width: 'auto' }} />
                    <div style={{ textAlign: 'center', flex: 1, paddingLeft: '16px', paddingRight: '16px' }}>
                      <h4 style={{ fontSize: '20px', fontWeight: 'bold', color: '#15803d', margin: 0 }}>
                        {takvimModal.hatimAdi}
                      </h4>
                      <p style={{ fontSize: '15px', color: '#4b5563', margin: '4px 0 0' }}>
                        {ad} {soyad} — {takvimModal.cuzNo}. Cüz Okuma Takvimi
                      </p>
                      <span style={{ display: 'inline-block', marginTop: '4px', padding: '4px 12px', backgroundColor: '#dcfce7', color: '#15803d', fontSize: '13px', borderRadius: '9999px' }}>
                        {takvimMode === 'donerli' ? '🔄 Dönerli' : '📌 Sabit'} {takvimTuru === 'daily' ? '📅 Günlük' : '📆 Haftalık'}
                      </span>
                    </div>
                    <img src={CihedLogo} alt="ÇİHED" style={{ height: '48px', width: 'auto' }} />
                  </div>

                  {/* Tablo - 2 sütun */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
                    {[firstHalf, secondHalf].map((half, halfIdx) => (
                      <div key={halfIdx} style={{ width: '100%' }}>
                        <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                          <thead>
                            <tr style={{ background: 'linear-gradient(to right, #dcfce7, #d1fae5)' }}>
                              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#166534', fontSize: '14px' }}>Tarih</th>
                              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#166534', fontSize: '14px' }}>Cüz</th>
                              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#166534', fontSize: '14px' }}>Sayfa</th>
                            </tr>
                          </thead>
                          <tbody>
                            {half.map((item, index) => (
                              <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f0fdf480' }}>
                                <td style={{ padding: '8px 14px', color: '#374151', fontSize: '13px' }}>{item.tarih}</td>
                                <td style={{ padding: '8px 14px', color: '#111827', fontWeight: '500', fontSize: '13px' }}>{item.cuz}. Cüz</td>
                                <td style={{ padding: '8px 14px', color: '#6b7280', fontSize: '13px' }}>s. {item.sayfalar}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>

                  {/* Alt footer */}
                  <div style={{ textAlign: 'center', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #bbf7d0' }}>
                    <p style={{ fontSize: '12px', color: '#16a34a', fontWeight: '500', margin: 0 }}>hatimtakip.com.tr</p>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex gap-3 rounded-b-2xl">
                <button
                  onClick={handleDownloadSchedule}
                  disabled={downloading}
                  className={`flex-1 py-3 px-4 font-bold rounded-xl flex items-center justify-center transition-all ${
                    downloading
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {downloading ? (
                    <>
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      İndiriliyor...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Takvimi İndir (PNG)
                    </>
                  )}
                </button>
                <button
                  onClick={() => setTakvimModal(null)}
                  className="py-3 px-6 border-2 border-gray-300 text-gray-600 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <FooterV2 />
    </>
  );
};

export default AdminGroupDetail;
